import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import emailToName from '../utils/emailToName';
import { useNavigate } from 'react-router-dom';
import cachedApi from '../utils/cachedApi';
import { socket } from '../socket';

// 공지사항 상세 모달
const NoticeDetailModal = ({ open, onClose, notice }: {
  open: boolean,
  onClose: () => void,
  notice: any
}) => {
  if (!open || !notice) return null;
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 10001 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 400, maxWidth: 600, minHeight: 400, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '15%', transform: 'translate(-50%, 0)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 10, color: '#1976d2', borderBottom: '1px solid #eee', paddingBottom: 8 }}>{notice.title}</div>
        <div style={{ color: '#444', fontSize: 16, lineHeight: 1.7, whiteSpace: 'pre-line', wordBreak: 'break-all', marginBottom: 18, overflowY: 'auto', maxHeight: '45vh' }}>{notice.content}</div>
        <div style={{ color: '#bbb', fontSize: 14, marginBottom: 18 }}>
          등록: {notice.createdAt?.toDate ? notice.createdAt.toDate().toLocaleString() : ''} / 작성자: {emailToName[notice.authorEmail] || notice.authorEmail}
        </div>
        <button style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 6, padding: '7px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }} onClick={onClose}>닫기</button>
      </div>
    </div>
  );
};

function useLatestNotices() {
  const [notices, setNotices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNotices = async () => {
    setLoading(true);
    try {
      // 캐시된 API 사용
      // const data = await cachedApi.getNotices({ limit: 3 });
      // setNotices(data);
      // ↓ 항상 Firestore에서 직접 조회
      const q = query(collection(db, 'notices'), orderBy('createdAt', 'desc'), limit(3));
      const snap = await getDocs(q);
      setNotices(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('공지사항 조회 실패:', error);
      setNotices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotices(); }, []);

  // [추가] 소켓 이벤트 구독하여 캐시 무효화 후 강제 reload
  useEffect(() => {
    const handleChange = () => {
      console.log('[NoticeWidget] 새 공지사항 이벤트 수신 - 강제 reload');
      fetchNotices(); // 파라미터 없이 호출
    };
    socket.on('notice-added', handleChange);
    socket.on('notice-updated', handleChange);
    socket.on('notice-deleted', handleChange);
    return () => {
      socket.off('notice-added', handleChange);
      socket.off('notice-updated', handleChange);
      socket.off('notice-deleted', handleChange);
    };
  }, []);

  return { notices, loading, reload: fetchNotices };
}

const NoticeListCard = ({ notices, onAll, onDetail }: { notices: any[], onAll: () => void, onDetail: (notice: any) => void }) => (
  <div style={{
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
    padding: '28px 32px 18px 32px',
    width: '100%',
    boxSizing: 'border-box',
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
    minHeight: 300,
  }}>
    <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <div style={{ fontWeight: 700, fontSize: 22, color: '#444444' }}>공지사항</div>
      <button style={{ background: '#eee', color: '#1976d2', border: 'none', borderRadius: 6, padding: '7px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }} onClick={onAll}>전체보기</button>
    </div>
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%', minHeight: 210 }}>
      {notices.map(notice => (
        <li key={notice.id} style={{ marginBottom: 12, cursor: 'pointer', minWidth: 0, width: '100%' }} onClick={() => onDetail(notice)}>
          <div style={{ fontWeight: 700, fontSize: 17, color: '#666666', marginBottom: 2, display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', minWidth: 0 }}>
            <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{notice.title}</span>
            {(() => {
              const now = new Date();
              const today = new Date(now);
              today.setHours(0, 0, 0, 0); // 오늘 시작 시간
              
              const tomorrow = new Date(now);
              tomorrow.setDate(tomorrow.getDate() + 1);
              tomorrow.setHours(23, 59, 59, 999); // 내일 마지막 시간
              
              const noticeDate = notice.createdAt?.toDate ? notice.createdAt.toDate() : new Date();
              
              // 오늘과 내일 작성된 글만 N 표시
              if (noticeDate >= today && noticeDate <= tomorrow) {
                return (
                  <span style={{
                    background: '#ff4444',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: 10,
                    minWidth: 16,
                    flexShrink: 0,
                    textAlign: 'center',
                    lineHeight: 1
                  }}>
                    N
                  </span>
                );
              }
              return null;
            })()}
          </div>
          <div style={{ color: '#444', fontSize: 15, marginBottom: 2, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.4, height: '1.4em' }}>{notice.content}</div>
          <div style={{ color: '#bbb', fontSize: 12, textAlign: 'right', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>등록: {notice.createdAt?.toDate ? notice.createdAt.toDate().toLocaleString() : ''} / {emailToName[notice.authorEmail] || notice.authorEmail}</div>
        </li>
      ))}
    </ul>
  </div>
);

const NoticeWidget = () => {
  const { notices, loading, reload } = useLatestNotices();
  const navigate = useNavigate();
  const [showDetail, setShowDetail] = useState(false);
  const [detailNotice, setDetailNotice] = useState<any>(null);

  // 전체보기 버튼 클릭 시 공지사항 게시판 페이지로 이동
  const handleAllClick = () => {
    navigate('/notice');
  };

  // 상세 보기
  const handleDetail = (notice: any) => {
    setDetailNotice(notice);
    setShowDetail(true);
  };

  return (
    <>
      <NoticeListCard notices={notices} onAll={handleAllClick} onDetail={handleDetail} />
      <NoticeDetailModal 
        open={showDetail} 
        onClose={() => setShowDetail(false)} 
        notice={detailNotice} 
      />
    </>
  );
};

export default NoticeWidget; 